import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// California Flag component
const CaliforniaFlag = ({ className = "w-8 h-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_green-builder-3/artifacts/dnderd21_Cali%20FLAG.png" 
    alt="California" 
    className={`${className} object-contain`}
  />
);

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="absolute inset-0 gradient-hero opacity-50" />
      
      <Card className="relative w-full max-w-md border-primary/20 bg-card/90 backdrop-blur-sm" data-testid="payment-success-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Processing Payment</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                Your CALGreen Waste Management Plan is ready for download.
              </p>
              
              {paymentData && (
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 mb-6">
                  <p className="text-sm text-muted-foreground">Amount Paid</p>
                  <p className="text-2xl font-bold text-primary">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="go-to-dashboard-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Go to Dashboard & Download
                </Button>
              </div>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6 border border-yellow-500/30">
                <CaliforniaFlag className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Payment Processing</h2>
              <p className="text-muted-foreground mb-6">
                Your payment is being processed. Please check your dashboard in a few moments.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {(status === 'error' || status === 'expired') && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6 border border-red-500/30">
                <CaliforniaFlag className="w-10 h-10 opacity-50" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.'
                  : 'We couldn\'t confirm your payment. Please check your dashboard or contact support.'}
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Go to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
